import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallButton from "@/components/CallButton";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOSubHero from "@/components/SEOSubHero";
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

const ZonesIntervention = () => {
  const villeurbanne = [
    { nom: "Charpennes-Charles Hernu", url: "/charpennes" },
    { nom: "République-Villeurbanne", url: "/republique" },
    { nom: "Gratte-Ciel", url: "/gratte-ciel" },
    { nom: "Cusset", url: "/cusset" },
    { nom: "Flachet", url: "/flachet" },
    { nom: "Laurent Bonnevay", url: "/laurent-bonnevay" },
    { nom: "Croix Luizet", url: "/croix-luizet" },
    { nom: "Ferrandière", url: "/ferrandiere" },
    { nom: "Maisons-Neuves", url: "/maisons-neuves" },
    { nom: "Perralière", url: "/perraliere" },
    { nom: "Salengro", url: "/salengro" },
    { nom: "Saint-Jean", url: "/saint-jean" },
    { nom: "Buers", url: "/buers" },
    { nom: "Tonkin", url: "/tonkin" },
    { nom: "Zola", url: "/zola" },
    { nom: "Tolstoï", url: "/tolstoi" },
    { nom: "Grand Clément", url: "/grand-clement" },
    { nom: "Château Gaillard", url: "/chateau-gaillard" }
  ];

  const lyon = [
    { arr: "Lyon 1", quartiers: "Pentes de la Croix-Rousse, Terreaux, Martinière", url: "/lyon-1" },
    { arr: "Lyon 2", quartiers: "Bellecour, Ainay, Confluence, Perrache", url: "/lyon-2" },
    { arr: "Lyon 3", quartiers: "Part-Dieu, Guillotière, Montchat, Sans Souci, Dauphiné", url: "/lyon-3" },
    { arr: "Lyon 4", quartiers: "Croix-Rousse", url: "/lyon-4" },
    { arr: "Lyon 5", quartiers: "Vieux Lyon, Fourvière, Point du Jour", url: "/lyon-5" },
    { arr: "Lyon 6", quartiers: "Brotteaux, Tête d'Or, Cité Internationale", url: "/lyon-6" },
    { arr: "Lyon 7", quartiers: "Gerland, Jean Macé, Guillotière", url: "/lyon-7" },
    { arr: "Lyon 8", quartiers: "Monplaisir, Montée de Choulans, États-Unis, Grange Blanche", url: "/lyon-8" },
    { arr: "Lyon 9", quartiers: "Vaise, Gorge de Loup, La Duchère", url: "/lyon-9" }
  ];

  const metropole = [
    { nom: "Bron", url: "/bron" },
    { nom: "Vaulx-en-Velin", url: "/vaulx-en-velin" },
    { nom: "Caluire-et-Cuire", url: "/caluire-et-cuire" },
    { nom: "Meyzieu", url: "/meyzieu" },
    { nom: "Décines-Charpieu", url: "/decines-charpieu" },
    { nom: "Rillieux-la-Pape", url: "/rillieux-la-pape" },
    { nom: "Vénissieux", url: "/venissieux" },
    { nom: "Saint-Priest", url: "/saint-priest" },
    { nom: "Tassin-la-Demi-Lune", url: "/tassin-la-demi-lune" },
    { nom: "Oullins", url: "/oullins" },
    { nom: "Pierre-Bénite", url: "/pierre-benite" },
    { nom: "Dardilly", url: "/dardilly" },
    { nom: "Écully", url: "/ecully" },
    { nom: "Saint-Genis-Laval", url: "/saint-genis-laval" },
    { nom: "Francheville", url: "/francheville" },
    { nom: "Sainte-Foy-lès-Lyon", url: "/sainte-foy-les-lyon" },
    { nom: "Neuville-sur-Saône", url: "/neuville-sur-saone" },
    { nom: "Fontaines-sur-Saône", url: "/fontaines-sur-saone" },
    { nom: "Sathonay-Camp", url: "/sathonay-camp" }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Zones d'Intervention Serrurier Villeurbanne & Lyon 69/01"
        description="Serrurier Tolstoï intervient à Villeurbanne (tous quartiers), Lyon (9 arrondissements) et métropole lyonnaise. Intervention rapide 24h/7j dans toute la zone 69/01."
        keywords="serrurier Villeurbanne quartiers, serrurier Lyon arrondissements, zone intervention 69, serrurier métropole Lyon"
        canonical="https://www.tolstoi-serrurier.fr/zones-intervention"
        ogUrl="https://www.tolstoi-serrurier.fr/zones-intervention"
        ogImage="https://www.tolstoi-serrurier.fr/logo.jpg"
        ogType="business.business"
      />
      <Header />
      
      <div className="container mx-auto px-4">
        <Breadcrumbs items={[
          { label: "Accueil", href: "/" },
          { label: "Zones d'intervention", href: "/zones-intervention" }
        ]} />
      </div>
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="bg-secondary text-secondary-foreground py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Zones d'Intervention 69/01
              </h1>
              <p className="text-xl mb-8 text-secondary-foreground/90">
                Serrurier Tolstoï intervient rapidement à Villeurbanne, Lyon et toute la métropole lyonnaise
              </p>
              <Button size="lg" variant="default" className="gap-2" asChild>
                <a href="tel:0482531608">
                  <Phone className="h-5 w-5" />
                  Appeler maintenant : 04 82 53 16 08
                </a>
              </Button>
            </div>
          </div>
        </section>

        <SEOSubHero title="Serrurier Villeurbanne & Grand Lyon — intervention express">
          <p>
            Basés à Villeurbanne, nous couvrons l'ensemble des quartiers et la <strong>métropole de Lyon</strong>. Délais optimisés selon votre secteur avec une disponibilité <strong>24h/7j</strong>.
          </p>
          <p>
            Nos services: <Link to="/ouverture-porte" className="text-primary hover:underline">ouverture de porte</Link>, <Link to="/changement-serrure" className="text-primary hover:underline">changement de serrure</Link>, <Link to="/reparation-serrure" className="text-primary hover:underline">réparation de serrure</Link>, <Link to="/volet-roulant" className="text-primary hover:underline">volet roulant</Link>, <Link to="/porte-blindee-garage" className="text-primary hover:underline">porte blindée & garage</Link>, et <Link to="/depannage-urgence" className="text-primary hover:underline">dépannage 24/7</Link>.
          </p>
        </SEOSubHero>

        {/* Main Content */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Intervention express dans toute la métropole</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Tolstoï Serrurier se déplace rapidement dans tous les quartiers de Villeurbanne, 
                  les 9 arrondissements de Lyon et dans toute la métropole lyonnaise.
                </p>
              </div>

              {/* Quick Stats */}
              <div className="grid md:grid-cols-3 gap-6 mb-16">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Clock className="h-12 w-12 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold text-2xl mb-2">30 min</h3>
                    <p className="text-muted-foreground">Temps d'intervention moyen</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <MapPin className="h-12 w-12 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold text-2xl mb-2">50+ villes</h3>
                    <p className="text-muted-foreground">Couverture métropole</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Phone className="h-12 w-12 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold text-2xl mb-2">24h/7j</h3>
                    <p className="text-muted-foreground">Disponibilité permanente</p>
                  </CardContent>
                </Card>
              </div>

              {/* Villeurbanne Section */}
              <Card className="mb-8">
                <CardHeader className="bg-primary/5">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-8 w-8 text-primary" />
                    <CardTitle className="text-2xl">Villeurbanne - Tous les quartiers</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-muted-foreground mb-4">
                    Notre base principale se situe à Villeurbanne. Nous intervenons dans tous les quartiers 
                    avec un délai d'intervention particulièrement rapide :
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {villeurbanne.map((quartier, index) => (
                      <Link 
                        key={index} 
                        to={quartier.url}
                        className="bg-muted/50 hover:bg-primary/10 p-3 rounded-lg text-sm transition-colors hover:shadow-md border border-transparent hover:border-primary/20"
                      >
                        {quartier.nom}
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Lyon Section */}
              <Card className="mb-8">
                <CardHeader className="bg-primary/5">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-8 w-8 text-primary" />
                    <CardTitle className="text-2xl">Lyon - Les 9 arrondissements</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-muted-foreground mb-4">
                    Intervention rapide dans tous les arrondissements de Lyon et leurs quartiers :
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {lyon.map((item, index) => (
                      <Link
                        key={index}
                        to={item.url}
                        className="bg-muted/50 hover:bg-primary/10 p-4 rounded-lg transition-colors hover:shadow-md border border-transparent hover:border-primary/20"
                      >
                        <h4 className="font-semibold mb-2">{item.arr}</h4>
                        <p className="text-sm text-muted-foreground">{item.quartiers}</p>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Métropole Section */}
              <Card className="mb-12">
                <CardHeader className="bg-primary/5">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-8 w-8 text-primary" />
                    <CardTitle className="text-2xl">Métropole de Lyon - Grand Lyon</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-muted-foreground mb-4">
                    Nous intervenons également dans toute la métropole de Lyon :
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {metropole.map((ville, index) => (
                      ville.url ? (
                        <Link
                          key={index}
                          to={ville.url}
                          className="bg-muted/50 hover:bg-primary/10 p-3 rounded-lg text-sm transition-colors hover:shadow-md border border-transparent hover:border-primary/20"
                        >
                          {ville.nom}
                        </Link>
                      ) : (
                        <div key={index} className="bg-muted/50 p-3 rounded-lg text-sm">
                          {ville.nom}
                        </div>
                      )
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-4 italic">
                    Et de nombreuses autres communes de la métropole lyonnaise...
                  </p>
                </CardContent>
              </Card>

              {/* CTA Section */}
              <div className="bg-secondary text-secondary-foreground rounded-xl p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Votre quartier n'est pas listé ?</h3>
                <p className="text-lg mb-6 text-secondary-foreground/90">
                  Appelez-nous ! Nous intervenons dans de nombreuses autres communes de la zone 69/01
                </p>
                <Button size="lg" variant="default" className="gap-2" asChild>
                  <a href="tel:0482531608">
                    <Phone className="h-5 w-5" />
                    04 82 53 16 08
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <CallButton />
    </div>
  );
};

export default ZonesIntervention;
